<script setup lang="ts">
import { onMounted, ref, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTenantRolesStore } from '@/@vxapp/TenantRoles/useTenantRolesStore'
import type { TenantRole, TenantRoleWritable } from '@/@vxapp/TenantRoles/useTenantRolesStore'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import type { AbilityAction } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import type { AbilitySubject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { canPageMeta } from '@/@layouts/plugins/casl'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import { useAccessContext, inaccessible } from '@/access/useAccessContext'
import TenantRoleForm from '@/components/Forms/TenantRoles/TenantRoleForm.vue'

definePage({ meta: PageMeta.TenantRoles.List.Meta })
setPageTitle('Tenant roles')

const store = useTenantRolesStore()
const route = useRoute()
const router = useRouter()
const rows = shallowRef<TenantRole[]>([])
const selected = ref<Partial<TenantRole> | null>(null)
const dialog = ref<'create' | 'edit' | 'delete' | null>(null)
const error = ref('')
const errors = ref<unknown>()
const success = ref('')
const loading = ref(false)
const denied = ref(false)
const search = ref('')
const page = ref(1)
const totalPages = ref(1)
let request = 0
let selectionRequest = 0

const context = useAccessContext(
  () => {
    request += 1
    selectionRequest += 1
    rows.value = []
    selected.value = null
    dialog.value = null
    error.value = ''
    errors.value = undefined
    success.value = ''
    loading.value = false
    denied.value = false
    search.value = ''
    page.value = 1
    totalPages.value = 1
  },
  undefined,
  load
)

function allowed(action: AbilityAction, subject: AbilitySubject) {
  return canPageMeta({ action, subject })
}
async function load() {
  const epoch = context.epoch()
  const version = ++request

  loading.value = true
  error.value = ''
  denied.value = false

  const result = await store.fetchAll({
    page: page.value,
    limit: 25,
    search: search.value || undefined,
  })

  if (!context.isCurrent(epoch) || version !== request) return
  loading.value = false
  if (result.responseCode !== 200 || !('tenantRoles' in result)) {
    rows.value = []
    denied.value = inaccessible(result.responseCode)
    error.value = result.message || 'Tenant roles could not be loaded.'

    return
  }
  rows.value = result.tenantRoles
  totalPages.value = Number(store.paginationMeta?.totalPages ?? 1)
}
async function syncQuery() {
  await router.replace({
    query: {
      ...(page.value > 1 ? { page: String(page.value) } : {}),
      ...(search.value ? { search: search.value } : {}),
    },
  })
}
function restoreQuery() {
  const incoming = Number(route.query.page)

  page.value = Number.isInteger(incoming) && incoming > 0 ? incoming : 1
  search.value = typeof route.query.search === 'string' ? route.query.search : ''
}
async function searchRows() {
  page.value = 1
  await syncQuery()
  await load()
}
async function previous() {
  page.value -= 1
  await syncQuery()
  await load()
}
async function next() {
  page.value += 1
  await syncQuery()
  await load()
}
function cancel() {
  selectionRequest += 1
  dialog.value = null
  selected.value = null
  errors.value = undefined
  error.value = ''
}
async function create() {
  if (!allowed(A.CREATE, Subject.TENANT_ROLES)) return
  cancel()

  const epoch = context.epoch()
  const version = ++selectionRequest
  const result = await store.fetchTemplate()
  if (!context.isCurrent(epoch) || version !== selectionRequest) return
  if (result.responseCode !== 200) {
    denied.value = inaccessible(result.responseCode)
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(store.template ?? {})
  dialog.value = 'create'
}
async function select(row: TenantRole, mode: 'edit' | 'delete') {
  cancel()

  const epoch = context.epoch()
  const version = ++selectionRequest
  const result = await store.fetch(row.roleGUID)
  if (!context.isCurrent(epoch) || version !== selectionRequest) return
  if (result.responseCode !== 200 || !('tenantRole' in result)) {
    denied.value = inaccessible(result.responseCode)
    if (denied.value) rows.value = []
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(result.tenantRole)
  dialog.value = mode
}
function edit(row: TenantRole) {
  if (allowed(A.UPDATE, Subject.TENANT_ROLES_UPDATE_GUID)) void select(row, 'edit')
}
function remove(row: TenantRole) {
  if (allowed(A.DELETE, Subject.TENANT_ROLES_DELETE_GUID)) void select(row, 'delete')
}
function view(row: TenantRole) {
  void router.push({ name: PageMeta.TenantRoles.Preview.Name, params: { guid: row.roleGUID } })
}
async function save(payload: TenantRoleWritable) {
  if (loading.value) return
  const mode = dialog.value
  const identifier = selected.value?.roleGUID
  if (mode !== 'create' && (mode !== 'edit' || !identifier)) return
  if (
    !allowed(
      mode === 'create' ? A.CREATE : A.UPDATE,
      mode === 'create' ? Subject.TENANT_ROLES : Subject.TENANT_ROLES_UPDATE_GUID
    )
  )
    return
  const epoch = context.epoch()

  loading.value = true

  const result =
    mode === 'create' ? await store.create(payload) : await store.update(identifier!, payload)

  if (!context.isCurrent(epoch)) return
  loading.value = false
  if (result.responseCode !== (mode === 'create' ? 201 : 200)) {
    error.value = result.message || 'The change could not be saved.'
    errors.value = result.errors
    if (inaccessible(result.responseCode)) {
      rows.value = []
      selected.value = null
      dialog.value = null
      denied.value = true
    }

    return
  }
  cancel()
  success.value = 'Saved successfully.'
  await load()
}
async function confirmDelete() {
  const identifier = selected.value?.roleGUID
  if (!identifier || loading.value || !allowed(A.DELETE, Subject.TENANT_ROLES_DELETE_GUID)) return
  const epoch = context.epoch()

  loading.value = true

  const result = await store.delete(identifier)
  if (!context.isCurrent(epoch)) return
  loading.value = false
  if (result.responseCode !== 200) {
    error.value = result.message || 'Deletion failed.'
    if (inaccessible(result.responseCode)) {
      rows.value = []
      selected.value = null
      dialog.value = null
      denied.value = true
    }

    return
  }
  cancel()
  success.value = 'Deleted successfully.'
  await load()
}
restoreQuery()
onMounted(load)
watch(
  () => route.query,
  () => {
    const previousQuery = `${page.value}:${search.value}`

    restoreQuery()
    if (`${page.value}:${search.value}` !== previousQuery) void load()
  }
)
</script>

<template>
  <main data-testid="tenant-roles-workspace">
    <h1 class="mb-4">Tenant roles</h1>
    <VAlert
      v-if="denied"
      type="error"
      data-testid="tenant-context-unavailable"
      >This resource is inaccessible in the current context.</VAlert
    >
    <VAlert
      v-else-if="error"
      type="error"
      class="mb-4"
      >{{ error }}</VAlert
    >
    <VAlert
      v-if="success"
      type="success"
      class="mb-4"
      >{{ success }}</VAlert
    >
    <VCard>
      <VCardText>
        <VForm
          class="d-flex flex-wrap gap-2 mb-4"
          role="search"
          @submit.prevent="searchRows"
        >
          <AppTextField
            v-model="search"
            label="Search tenant roles"
            :rules="[]"
          />
          <VBtn
            type="submit"
            :disabled="loading"
            >Search</VBtn
          >
          <VBtn
            v-if="allowed(A.CREATE, Subject.TENANT_ROLES)"
            :disabled="loading"
            @click="create"
            >Create role</VBtn
          >
        </VForm>
        <VProgressLinear
          v-if="loading"
          indeterminate
          aria-label="Loading tenant roles"
        />
        <p v-if="!loading && !error && !denied && rows.length === 0">No tenant roles found.</p>
        <VTable
          v-if="rows.length"
          density="comfortable"
        >
          <thead>
            <tr>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.roleGUID"
              :data-testid="`tenant-role-row-${row.roleGUID}`"
            >
              <td>{{ row.name }}</td>
              <td>
                <div class="d-flex flex-wrap gap-1">
                  <VBtn
                    v-if="allowed(A.READ, Subject.TENANT_ROLES_GUID)"
                    size="small"
                    @click="() => view(row)"
                    >View</VBtn
                  >
                  <VBtn
                    v-if="allowed(A.UPDATE, Subject.TENANT_ROLES_UPDATE_GUID)"
                    size="small"
                    :disabled="loading"
                    @click="() => edit(row)"
                    >Edit</VBtn
                  >
                  <VBtn
                    v-if="allowed(A.DELETE, Subject.TENANT_ROLES_DELETE_GUID)"
                    size="small"
                    color="error"
                    :disabled="loading"
                    @click="() => remove(row)"
                    >Delete</VBtn
                  >
                </div>
              </td>
            </tr>
          </tbody>
        </VTable>
        <div class="d-flex flex-wrap align-center gap-2 mt-4">
          <VBtn
            :disabled="loading || page <= 1"
            @click="previous"
            >Previous</VBtn
          ><span>Page {{ page }} of {{ totalPages }}</span
          ><VBtn
            :disabled="loading || page >= totalPages"
            @click="next"
            >Next</VBtn
          >
        </div>
      </VCardText>
    </VCard>
    <VDialog
      :model-value="dialog !== null"
      max-width="700"
      persistent
    >
      <VCard v-if="selected">
        <VCardTitle>{{
          dialog === 'delete' ? 'Delete role?' : dialog === 'create' ? 'Create role' : 'Edit role'
        }}</VCardTitle>
        <VCardText>
          <VAlert
            v-if="error"
            type="error"
            class="mb-4"
            >{{ error }}</VAlert
          >
          <TenantRoleForm
            v-if="dialog !== 'delete'"
            :role="selected"
            :is-edit="dialog === 'edit'"
            :loading="loading"
            :errors="errors"
            @submit="save"
            @cancel="cancel"
          />
          <template v-else
            ><p>Delete this role from the current tenant?</p>
            <VBtn
              color="error"
              :loading="loading"
              @click="confirmDelete"
              >Confirm delete</VBtn
            ><VBtn
              :disabled="loading"
              @click="cancel"
              >Cancel</VBtn
            ></template
          >
        </VCardText>
      </VCard>
    </VDialog>
  </main>
</template>
